import { useEffect, useState } from "react";
import { ArrowRight, MapPin } from "lucide-react";
import { CornerMarks, StatusLED } from "@/components/infra";
import { CompanyMark } from "@/components/jobs/CompanyMark";
import { LogoMark } from "@/components/hero/HeroShared";
import { BHeroCopy, B_GRID_BG } from "@/components/hero/BHeroCopy";
import { fmt, timeLabel } from "@/components/hero/LiveIndex";
import { useHeroLiveStats } from "@/lib/useHeroLiveStats";

const FEED_SIZE = 5;
const ago = (iso) => {
  if (!iso) return "";
  const minutes = Math.max(1, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (Number.isNaN(minutes)) return "";
  if (minutes < 60) return `${minutes}m ago`;
  if (minutes < 1440) return `${Math.round(minutes / 60)}h ago`;
  return `${Math.round(minutes / 1440)}d ago`;
};

const usePulse = (count) => {
  const [active, setActive] = useState(0);
  useEffect(() => {
    if (!count || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const timer = setInterval(() => setActive((current) => (current + 1) % count), 2400);
    return () => clearInterval(timer);
  }, [count]);
  return active;
};

const RoleRow = ({ job, active, isNewest }) => (
  <li data-testid={`hero-option-b5-role-${job.id}`} className={`flex items-center gap-3 px-4 sm:px-5 py-3 transition-colors duration-500 ${active ? "bg-[#EFFBF5]" : "bg-transparent"}`}>
    <CompanyMark company={job.company} size="sm" />
    <span className="min-w-0 flex-1">
      <span className="block text-sm font-semibold text-[#0F1117] truncate">{job.title}</span>
      <span className="flex items-center gap-1 text-xs text-[#5F6168] min-w-0"><span className="shrink-0 max-w-[50%] truncate">{job.company}</span><span aria-hidden="true">·</span><MapPin size={11} aria-hidden="true" className="shrink-0" /><span className="truncate">{job.location}</span></span>
    </span>
    <span className="shrink-0 flex flex-col items-end gap-1">
      {isNewest && <span className="font-mono-label text-[9px] !tracking-normal text-white bg-[#078552] px-1.5 py-0.5">New</span>}
      <span className="text-[10px] text-[#71747B] whitespace-nowrap">{ago(job.date || job.checked_at)}</span>
    </span>
  </li>
);

const PulseCard = ({ live }) => {
  const roles = live.jobs?.jobs?.slice(0, FEED_SIZE) || [];
  const active = usePulse(roles.length);
  const updated = timeLabel(live.jobs?.checked_at);
  return (
    <section data-testid="hero-option-b5-pulse" aria-label="Hiring now across digital infrastructure" className="chrome-surface relative min-w-0 fade-up delay-200">
      <CornerMarks color="#A9ADB4" />
      <div className="flex items-center justify-between gap-3 px-4 sm:px-5 py-3 border-b hairline">
        <span className="inline-flex items-center gap-2 font-mono-label text-[11px] !tracking-normal text-[#0F1117]"><StatusLED color="#10B981" />Hiring Now</span>
        <span data-testid="hero-option-b5-updated" className="font-mono-label text-[10px] !tracking-normal text-[#5F6168]">{live.loading ? "Syncing…" : updated ? `Feed checked ${updated}` : "Live feed"}</span>
      </div>
      <div className="grid grid-cols-[96px_1fr] sm:grid-cols-[130px_1fr] items-center gap-5 px-4 sm:px-5 py-5 border-b hairline">
        <LogoMark testId="hero-option-b5-mark" className="w-full dcyb-mark-shadow" />
        <div className="min-w-0">
          <p data-testid="hero-option-b5-total" className="font-display text-4xl sm:text-5xl font-bold text-[#0F1117] leading-none">{fmt(live.jobs?.feed_total)}</p>
          <p className="mt-2 font-mono-label text-[10px] !tracking-normal text-[#3D4049]">Open roles · {fmt(live.jobs?.employer_count)} employer feeds</p>
          <p data-testid="hero-option-b5-early" className="mt-1 text-xs text-[#078552] font-semibold">{fmt(live.jobs?.early_career_total)} early-career roles</p>
        </div>
      </div>
      <p className="px-4 sm:px-5 pt-3 font-mono-label text-[10px] !tracking-normal text-[#5F6168]">Newest roles</p>
      <ul data-testid="hero-option-b5-feed" aria-live="off" className="divide-y divide-[#E3E5E9] min-h-[120px]">
        {roles.map((job, index) => <RoleRow key={job.id} job={job} active={index === active} isNewest={index === 0} />)}
        {!live.loading && roles.length === 0 && <li className="px-5 py-6 text-sm text-[#5F6168]">The live feed is refreshing. Browse the board for every open role.</li>}
      </ul>
      <a href="/#jobs" data-testid="hero-option-b5-browse" className="group flex items-center justify-between gap-3 px-4 sm:px-5 py-3.5 border-t hairline font-mono-label text-[11px] !tracking-normal text-[#0F1117] hover:bg-[#F4F5F7] transition-colors">
        Browse all {fmt(live.jobs?.feed_total)} roles<ArrowRight size={14} aria-hidden="true" className="transition-transform group-hover:translate-x-0.5" />
      </a>
    </section>
  );
};

export const HeroOptionB5 = () => {
  const live = useHeroLiveStats({ jobsCount: FEED_SIZE });
  return (
    <section data-testid="hero-option-b5" className="relative overflow-hidden border-b hairline bg-white">
      <div aria-hidden="true" className="absolute inset-0 pointer-events-none opacity-[0.5]" style={B_GRID_BG} />
      <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-12 sm:py-16 lg:py-16 grid grid-cols-1 lg:grid-cols-12 gap-x-12 gap-y-12 items-center">
        <BHeroCopy prefix="hero-option-b5" eyebrow="Hiring now across digital infrastructure" className="lg:col-span-7" />
        <div className="lg:col-span-5 min-w-0"><PulseCard live={live} /></div>
      </div>
    </section>
  );
};
